import { useState, useEffect } from 'react';
import { Trip } from '../types';
import { tripService } from '../services/trips';

export const useTrips = () => {
  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTrips = async () => {
    try {
      console.log('🔄 useTrips: Fetching trips...');
      setLoading(true);
      setError(null);
      const data = await tripService.getTrips();
      console.log('✅ useTrips: Trips fetched successfully:', data);
      setTrips(data);
    } catch (err: any) {
      console.error('❌ useTrips: Error fetching trips:', err);
      setError(err.message);
      // Don't throw the error, just log it
    } finally {
      setLoading(false);
    }
  };

  const addTrip = async (tripData: any) => {
    try {
      console.log('🔄 useTrips: Creating trip...', tripData);
      const newTrip = await tripService.createTrip(tripData);
      console.log('✅ useTrips: Trip created successfully:', newTrip);
      setTrips(prev => [...prev, newTrip]);
      return newTrip;
    } catch (err: any) {
      console.error('❌ useTrips: Error creating trip:', err);
      setError(err.message);
      throw err;
    }
  };

  const updateTrip = async (id: number, tripData: any) => {
    try {
      console.log('🔄 useTrips: Updating trip...', id, tripData);
      const updatedTrip = await tripService.updateTrip(id, tripData);
      console.log('✅ useTrips: Trip updated successfully:', updatedTrip);
      setTrips(prev => 
        prev.map(trip => 
          trip.id === id ? updatedTrip : trip
        )
      );
      return updatedTrip;
    } catch (err: any) {
      console.error('❌ useTrips: Error updating trip:', err);
      setError(err.message);
      throw err;
    }
  };

  const deleteTrip = async (id: number) => {
    try {
      console.log('🔄 useTrips: Deleting trip...', id);
      await tripService.deleteTrip(id);
      console.log('✅ useTrips: Trip deleted successfully');
      setTrips(prev => prev.filter(trip => trip.id !== id));
    } catch (err: any) {
      console.error('❌ useTrips: Error deleting trip:', err);
      setError(err.message);
      throw err;
    }
  };

  const confirmPayment = async (tripId: number, paymentData: { gateway_transaction_id: string; payment_method: string }) => {
    try {
      console.log('🔄 useTrips: Confirming payment...', tripId, paymentData);
      const updatedTrip = await tripService.confirmPayment(tripId, paymentData);
      console.log('✅ useTrips: Payment confirmed successfully:', updatedTrip);
      setTrips(prev => 
        prev.map(trip => 
          trip.id === tripId ? updatedTrip : trip
        )
      );
      return updatedTrip;
    } catch (err: any) {
      console.error('❌ useTrips: Error confirming payment:', err);
      setError(err.message);
      throw err;
    }
  };

  useEffect(() => {
    console.log('🔄 useTrips: Component mounted, fetching trips');
    fetchTrips();
  }, []);

  return {
    trips,
    loading,
    error,
    fetchTrips,
    addTrip,
    updateTrip,
    deleteTrip,
    confirmPayment,
  };
};

export const useTrip = (id: number) => {
  const [trip, setTrip] = useState<Trip | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTrip = async () => {
    try {
      console.log('🔄 useTrip: Fetching trip...', id);
      setLoading(true);
      setError(null);
      const data = await tripService.getTrip(id);
      console.log('✅ useTrip: Trip fetched successfully:', data);
      setTrip(data);
    } catch (err: any) {
      console.error('❌ useTrip: Error fetching trip:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      console.log('🔄 useTrip: Component mounted with ID, fetching trip:', id);
      fetchTrip();
    }
  }, [id]);

  return {
    trip,
    loading,
    error,
    fetchTrip,
  };
};
